/**
 * This file helps pick the right M3 ending from PDM data.
 *
 * Some vehicles can have more than one possible M3 code.
 * So we look at the user's current choices, like trim, body type,
 * middle row, back row, and driver seat, and then choose the best match.
 */

import type { PdmM3LookupRow, StepConfiguration } from "@views/Upholstery/types"
import type { PdmFlatRow } from "../../pdm/normalizePdm"
import { getSeatAndRowSlices, unwrapValue } from "../../pdm/normalizePdm"
import { isPdmStructureV2Enabled } from "../flags/isPdmStructureV2Enabled"
import {
  getPreferredBodyRowsM3Sku,
  isM3BodyRowsSkuSegment,
  normalizeOptionMatchKey,
} from "./pdm-option-sku"

export type { PdmM3LookupRow }

// Get the base part number and clean it up.
function productBase(product: Record<string, unknown>): string {
  return String(product.base_part_number ?? product.basePartNumber ?? "")
    .replace(/\s+/g, "")
    .trim()
}

// Remove the vehicle SKU from the front and keep only the ending.
export function skuSuffixForBase(base: string, vehicleSku: string): string {
  const v = vehicleSku.replace(/\s+/g, "").trim()
  if (v && base.startsWith(v) && base.length > v.length) {
    return base.slice(v.length)
  }
  return base
}

/** Vehicle SKU + option remainder. Used only when PDM lookup has no match. */
function appendVehicleAndRemainder(
  vehicleSku: string | undefined,
  remainder: string
): string {
  const vehicle = (vehicleSku ?? "").replace(/\s+/g, "")
  const bodyRows = remainder.replace(/\s+/g, "")
  if (!bodyRows) return vehicle
  if (!vehicle) return bodyRows
  if (bodyRows.startsWith(vehicle)) return bodyRows
  return vehicle + bodyRows
}

// Get the selected value for one field.
// If there is no value, use the label instead.
function substepValue(
  configurations: StepConfiguration | undefined,
  stepKey: string,
  substepKey: string
): string {
  const ss = configurations?.[stepKey]?.substeps?.[substepKey]
  return (ss?.value || ss?.label || "").trim()
}

// Check if a PDM value matches what the user selected.
// This also works when one value contains a list like "A, B".
function valuesAlign(pdmValue: string, selected: string): boolean {
  if (!pdmValue || !selected) return false
  const pdmKey = normalizeOptionMatchKey(pdmValue)
  const selectedKey = normalizeOptionMatchKey(selected)
  if (pdmKey && pdmKey === selectedKey) return true

  const pdmParts = pdmValue
    .split(",")
    .map((part) => normalizeOptionMatchKey(part))
    .filter(Boolean)
  const selectedParts = selected
    .split(",")
    .map((part) => normalizeOptionMatchKey(part))
    .filter(Boolean)

  return pdmParts.some((pdmPart) =>
    selectedParts.some((selectedPart) => pdmPart === selectedPart)
  )
}

// Filter the possible rows by one selected value.
// If that would remove everything, keep the old list.
function applySoftFilter(
  rows: PdmM3LookupRow[],
  selected: string,
  getPdmValue: (row: PdmM3LookupRow) => string
): PdmM3LookupRow[] {
  if (!selected || !rows.length) return rows
  const next = rows.filter((row) => {
    const pdmValue = getPdmValue(row)
    if (!pdmValue) return true
    return valuesAlign(pdmValue, selected)
  })
  return next.length ? next : rows
}

// Turn the raw PDM rows into a simpler lookup list.
// Each item keeps the values we need later for matching.
export function buildPdmM3Lookup(
  flat: PdmFlatRow[],
  vehicleSku: string
): PdmM3LookupRow[] {
  const out: PdmM3LookupRow[] = []
  const seen = new Set<string>()

  for (const row of flat) {
    const base = productBase(row.product)
    if (!base) continue
    const suffix = skuSuffixForBase(base, vehicleSku)
    if (!suffix || !isM3BodyRowsSkuSegment(suffix)) continue

    const seats = getSeatAndRowSlices(row.product)
    const entry: PdmM3LookupRow = {
      base,
      suffix,
      trim: unwrapValue(
        row.application.sub_model ?? row.application.SubModel ?? ""
      ),
      bodyType: unwrapValue(
        row.application.body_type ??
          row.application.bodyType ??
          row.application.BodyType ??
          ""
      ),
      front: seats.frontRow,
      middle: seats.middleRow,
      back: seats.backRow,
      driverSeat: seats.driverSeat,
      passengerSeat: seats.passengerSeat,
    }

    const key = [
      entry.base,
      entry.trim,
      entry.bodyType,
      entry.front,
      entry.middle,
      entry.back,
      entry.driverSeat,
      entry.passengerSeat,
    ].join("|")
    if (seen.has(key)) continue
    seen.add(key)
    out.push(entry)
  }

  return out
}

function selectedBodyRows(configurations: StepConfiguration | undefined) {
  return {
    trim: substepValue(configurations, "body", "trim"),
    bodyType: substepValue(configurations, "body", "body-step"),
    middle: substepValue(configurations, "rows", "middle"),
    back: substepValue(configurations, "rows", "back"),
    driverSeat: substepValue(configurations, "body", "driver-seat-type"),
    passengerSeat: substepValue(configurations, "body", "passenger-seat-type"),
  }
}

function scoreLookupRow(
  row: PdmM3LookupRow,
  selected: ReturnType<typeof selectedBodyRows>
): number {
  let n = 0
  if (selected.back && row.back && valuesAlign(row.back, selected.back)) n += 8
  if (selected.middle && row.middle && valuesAlign(row.middle, selected.middle))
    n += 8
  if (
    selected.bodyType &&
    row.bodyType &&
    valuesAlign(row.bodyType, selected.bodyType)
  )
    n += 4
  if (selected.trim && row.trim && valuesAlign(row.trim, selected.trim)) n += 2
  if (
    selected.driverSeat &&
    row.driverSeat &&
    valuesAlign(row.driverSeat, selected.driverSeat)
  )
    n += 8
  if (
    selected.passengerSeat &&
    row.passengerSeat &&
    valuesAlign(row.passengerSeat, selected.passengerSeat)
  )
    n += 8
  return n
}

/** Best PDM product row for the current body/rows choices. */
export function pickBestPdmM3LookupRow(
  lookup: PdmM3LookupRow[] | undefined,
  configurations: StepConfiguration | undefined
): PdmM3LookupRow | null {
  if (!lookup?.length || !configurations) return null

  const selected = selectedBodyRows(configurations)
  let rows = lookup
  rows = applySoftFilter(rows, selected.back, (row) => row.back)
  rows = applySoftFilter(rows, selected.middle, (row) => row.middle)
  rows = applySoftFilter(rows, selected.bodyType, (row) => row.bodyType)
  rows = applySoftFilter(rows, selected.trim, (row) => row.trim)
  rows = applySoftFilter(
    rows,
    selected.driverSeat,
    (row) => row.driverSeat ?? ""
  )
  rows = applySoftFilter(
    rows,
    selected.passengerSeat,
    (row) => row.passengerSeat ?? ""
  )
  if (!rows.length) return null

  return rows.reduce((winner, row) =>
    scoreLookupRow(row, selected) > scoreLookupRow(winner, selected)
      ? row
      : winner
  )
}

// Choose the best M3 ending for the current selections.
// Back row, middle row, and seat type are the strongest signals.
export function resolvePdmM3SuffixFromSelections(
  lookup: PdmM3LookupRow[] | undefined,
  configurations: StepConfiguration | undefined,
  vehicleSku: string
): string {
  const best = pickBestPdmM3LookupRow(lookup, configurations)
  if (!best) return ""
  const suffix = skuSuffixForBase(best.base, vehicleSku)
  return isM3BodyRowsSkuSegment(suffix) ? suffix : ""
}

/**
 * Full M3 prefix for this vehicle and the current choices.
 *
 * When PDM lookup finds a product, use that product's `base_part_number`
 * as-is. Do not strip it and glue it back onto Dato `vehicle.sku`.
 * That is how mixed-make kits (CHE2408E vs GMC26013) stay correct.
 *
 * When there is no PDM match, keep the older rule: vehicle SKU + option remainder.
 */
export function getM3PrefixForVehicle(
  configurations: StepConfiguration,
  vehicle: { sku?: string; pdmM3Lookup?: PdmM3LookupRow[] } | null | undefined
): string {
  const lookup = vehicle?.pdmM3Lookup
  if (lookup?.length && isPdmStructureV2Enabled()) {
    const best = pickBestPdmM3LookupRow(lookup, configurations)
    const pdmBase = (best?.base ?? "").replace(/\s+/g, "")
    if (pdmBase && isM3BodyRowsSkuSegment(pdmBase)) {
      return pdmBase
    }
  }

  const remainder = getPreferredBodyRowsM3Sku(configurations)
  return appendVehicleAndRemainder(vehicle?.sku, remainder)
}
